// // screens/marketplace/pricingApi.js
// //
// // Thin wrappers around the marketplace pricing endpoints.
// //
// // Backend contract (from the API spec):
// //   POST   /marketplace/pricing/           → create OR update (idempotent by (product, user, price_type))
// //   PATCH  /marketplace/pricing/{id}/      → update price + notes only
// //   DELETE /marketplace/pricing/{id}/      → remove override (fallback applies)
// //   GET    /marketplace/pricing/           → paginated list, filter by product/user/price_type
// //
// // Every caller passes the raw `authFetch` so this module stays independent
// // of the AuthProvider shape.

// export const PRICE_TYPES = {
//   // Admin sets for a specific agent
//   ADMIN_AGENT_CUSTOM: {
//     value: "ADMIN_AGENT_CUSTOM",
//     label: "Agent cost (admin override)",
//     scope: "agent-cost",
//     isPreorder: false,
//     setBy: ["ADMIN"],
//   },
//   ADMIN_AGENT_PRE_ORDER_CUSTOM: {
//     value: "ADMIN_AGENT_PRE_ORDER_CUSTOM",
//     label: "Agent pre-order cost (admin override)",
//     scope: "agent-cost",
//     isPreorder: true,
//     setBy: ["ADMIN"],
//   },

//   // Agent sets for their walk-in customers
//   AGENT_CUSTOMER_PRICE: {
//     value: "AGENT_CUSTOMER_PRICE",
//     label: "Customer price",
//     scope: "customer-facing",
//     isPreorder: false,
//     setBy: ["ADMIN", "AGENT"],
//   },
//   AGENT_PRE_ORDER_CUSTOMER_PRICE: {
//     value: "AGENT_PRE_ORDER_CUSTOMER_PRICE",
//     label: "Pre-order customer price",
//     scope: "customer-facing",
//     isPreorder: true,
//     setBy: ["ADMIN", "AGENT"],
//   },

//   // Agent sets for all sub-agents under them
//   AGENT_SUB_BASE_PRICE: {
//     value: "AGENT_SUB_BASE_PRICE",
//     label: "Sub-agent base cost",
//     scope: "sub-agent-cost",
//     isPreorder: false,
//     setBy: ["ADMIN", "AGENT"],
//   },
//   AGENT_SUB_PRE_ORDER_BASE_PRICE: {
//     value: "AGENT_SUB_PRE_ORDER_BASE_PRICE",
//     label: "Sub-agent pre-order base cost",
//     scope: "sub-agent-cost",
//     isPreorder: true,
//     setBy: ["ADMIN", "AGENT"],
//   },

//   // Agent sets for a specific sub-agent
//   AGENT_SUB_CUSTOM_PRICE: {
//     value: "AGENT_SUB_CUSTOM_PRICE",
//     label: "Sub-agent cost (individual)",
//     scope: "sub-agent-cost-override",
//     isPreorder: false,
//     setBy: ["ADMIN", "AGENT"],
//   },
//   AGENT_SUB_PRE_ORDER_CUSTOM_PRICE: {
//     value: "AGENT_SUB_PRE_ORDER_CUSTOM_PRICE",
//     label: "Sub-agent pre-order cost (individual)",
//     scope: "sub-agent-cost-override",
//     isPreorder: true,
//     setBy: ["ADMIN", "AGENT"],
//   },

//   // Sub-agent sets for their walk-in customers
//   SUB_AGENT_CUSTOMER_PRICE: {
//     value: "SUB_AGENT_CUSTOMER_PRICE",
//     label: "Customer price",
//     scope: "customer-facing",
//     isPreorder: false,
//     setBy: ["ADMIN", "SUB_AGENT"],
//   },
//   SUB_AGENT_PRE_ORDER_CUSTOMER_PRICE: {
//     value: "SUB_AGENT_PRE_ORDER_CUSTOMER_PRICE",
//     label: "Pre-order customer price",
//     scope: "customer-facing",
//     isPreorder: true,
//     setBy: ["ADMIN", "SUB_AGENT"],
//   },
// };

// export const priceTypeMeta = (value) =>
//   Object.values(PRICE_TYPES).find((t) => t.value === value) || {
//     value,
//     label: value,
//     scope: "unknown",
//     isPreorder: false,
//     setBy: [],
//   };

// /**
//  * Save a price override.
//  *
//  * Because the backend uses update_or_create keyed on (product, user, price_type),
//  * we can always POST — no need to know in advance whether a row exists.
//  * This removes the entire "should I PATCH or POST?" branch from the UI.
//  *
//  * @param {function} authFetch
//  * @param {object} payload  { product, user, price_type, price, notes? }
//  * @returns {Promise<object>} resolved with the saved pricing object
//  * @throws {Error} with a human-readable message on any failure
//  */
// export const savePricing = async (authFetch, payload) => {
//   const res = await authFetch("/marketplace/pricing/", {
//     method: "POST",
//     body: JSON.stringify(payload),
//   });
//   const data = await res.json();
//   if (!res.ok) {
//     throw new Error(readApiError(data, "Could not save price"));
//   }
//   return data.data;
// };

// /**
//  * Update an existing price override. Only price + notes can change.
//  */
// export const updatePricing = async (authFetch, pricingId, { price, notes }) => {
//   const res = await authFetch(`/marketplace/pricing/${pricingId}/`, {
//     method: "PATCH",
//     body: JSON.stringify({ price, notes }),
//   });
//   const data = await res.json();
//   if (!res.ok) {
//     throw new Error(readApiError(data, "Could not update price"));
//   }
//   return data.data;
// };

// /**
//  * Delete a price override. Fallback chain takes over from the next level up.
//  */
// export const deletePricing = async (authFetch, pricingId) => {
//   const res = await authFetch(`/marketplace/pricing/${pricingId}/`, {
//     method: "DELETE",
//   });
//   if (!res.ok) {
//     const data = await res.json().catch(() => ({}));
//     throw new Error(readApiError(data, "Could not remove price"));
//   }
//   return true;
// };

// /**
//  * Fetch a page of pricing overrides. Server filters by query params.
//  */
// export const listPricing = async (authFetch, filters = {}) => {
//   const params = new URLSearchParams();
//   Object.entries(filters).forEach(([k, v]) => {
//     if (v !== undefined && v !== null && v !== "") params.set(k, v);
//   });
//   const qs = params.toString();
//   const res = await authFetch(`/marketplace/pricing/${qs ? `?${qs}` : ""}`);
//   const data = await res.json();
//   if (!res.ok) {
//     throw new Error(readApiError(data, "Could not load pricing"));
//   }
//   return data.data || [];
// };

// /**
//  * Extract the human-readable error message from any of the
//  * pricing endpoint's error shapes.
//  */
// const readApiError = (data, fallback) => {
//   if (!data) return fallback;
//   if (data.message) return data.message;
//   const errs = data.errors;
//   if (errs) {
//     // Flatten `{"non_field_errors": ["..."]}` or field-keyed maps
//     const parts = [];
//     for (const [key, val] of Object.entries(errs)) {
//       const arr = Array.isArray(val) ? val : [val];
//       arr.forEach((msg) => {
//         if (key === "non_field_errors" || key === "detail") parts.push(msg);
//         else parts.push(`${key.replace(/_/g, " ")}: ${msg}`);
//       });
//     }
//     if (parts.length) return parts.join(" ");
//   }
//   return fallback;
// };

// /**
//  * Which price types can the current role create for a given context?
//  *
//  * @param {string} role        "ADMIN" | "AGENT" | "SUB_AGENT" | ...
//  * @param {"customer"|"subAgent"} context
//  * @returns {object[]} list of price-type metas
//  */
// export const availablePriceTypes = (role, context) => {
//   if (context === "customer") {
//     if (role === "ADMIN" || role === "AGENT") {
//       return [PRICE_TYPES.AGENT_CUSTOMER_PRICE, PRICE_TYPES.AGENT_PRE_ORDER_CUSTOMER_PRICE];
//     }
//     if (role === "SUB_AGENT") {
//       return [PRICE_TYPES.SUB_AGENT_CUSTOMER_PRICE, PRICE_TYPES.SUB_AGENT_PRE_ORDER_CUSTOMER_PRICE];
//     }
//   }
//   if (context === "subAgent" && (role === "ADMIN" || role === "AGENT")) {
//     return [
//       PRICE_TYPES.AGENT_SUB_BASE_PRICE,
//       PRICE_TYPES.AGENT_SUB_PRE_ORDER_BASE_PRICE,
//       PRICE_TYPES.AGENT_SUB_CUSTOM_PRICE,
//       PRICE_TYPES.AGENT_SUB_PRE_ORDER_CUSTOM_PRICE,
//     ];
//   }
//   return [];
// };

// /**
//  * Render a fallback-aware "effective price" for a product.
//  * Mirrors the resolver logic from the backend, from the caller's view.
//  *
//  * This is the number a buyer sees, given the current user's role.
//  */
// export const resolveEffectivePrice = (product, overrides, userRole) => {
//   // overrides is an array of pricing rows already filtered to this product.
//   const find = (type) =>
//     overrides.find((o) => o.price_type === type)?.price ?? null;

//   if (userRole === "SUB_AGENT") {
//     return (
//       find("SUB_AGENT_CUSTOMER_PRICE") ??
//       find("AGENT_SUB_CUSTOM_PRICE") ??
//       find("AGENT_SUB_BASE_PRICE") ??
//       find("ADMIN_AGENT_CUSTOM") ??
//       product.base_price_agent
//     );
//   }

//   if (userRole === "AGENT") {
//     return (
//       find("AGENT_CUSTOMER_PRICE") ??
//       find("ADMIN_AGENT_CUSTOM") ??
//       product.base_price_agent
//     );
//   }

//   return product.base_price_customer;
// };






















// screens/marketplace/pricingApi.js
//
// Thin wrappers around the marketplace pricing endpoints.
//
// Backend contract (from the API spec):
//   POST   /marketplace/pricing/           → create OR update
//                                             (idempotent by (product, variant_key, user, price_type))
//   PATCH  /marketplace/pricing/{id}/      → update price + notes only
//   DELETE /marketplace/pricing/{id}/      → remove override (fallback applies)
//   GET    /marketplace/pricing/           → paginated list, filter by
//                                             product / variant_key / user / price_type
//
// Every caller passes the raw `authFetch` so this module stays independent
// of the AuthProvider shape.
//
// Variant awareness (v2):
//   • Every pricing row may carry a `variant_key` string.
//     - "" or null  → product-level override
//     - "Color=Red|Size=L" → variant-level override
//   • The write endpoints accept `variant_key` as an optional field.
//     Omitting it is equivalent to "" (product-level).
//   • Reads filter by `variant_key` when provided.

export const PRICE_TYPES = {
  // Admin sets for a specific agent
  ADMIN_AGENT_CUSTOM: {
    value: "ADMIN_AGENT_CUSTOM",
    label: "Agent cost (admin override)",
    scope: "agent-cost",
    isPreorder: false,
    setBy: ["ADMIN"],
  },
  ADMIN_AGENT_PRE_ORDER_CUSTOM: {
    value: "ADMIN_AGENT_PRE_ORDER_CUSTOM",
    label: "Agent pre-order cost (admin override)",
    scope: "agent-cost",
    isPreorder: true,
    setBy: ["ADMIN"],
  },

  // Agent sets for their walk-in customers
  AGENT_CUSTOMER_PRICE: {
    value: "AGENT_CUSTOMER_PRICE",
    label: "Customer price",
    scope: "customer-facing",
    isPreorder: false,
    setBy: ["ADMIN", "AGENT"],
  },
  AGENT_PRE_ORDER_CUSTOMER_PRICE: {
    value: "AGENT_PRE_ORDER_CUSTOMER_PRICE",
    label: "Pre-order customer price",
    scope: "customer-facing",
    isPreorder: true,
    setBy: ["ADMIN", "AGENT"],
  },

  // Agent sets for all sub-agents under them
  AGENT_SUB_BASE_PRICE: {
    value: "AGENT_SUB_BASE_PRICE",
    label: "Sub-agent base cost",
    scope: "sub-agent-cost",
    isPreorder: false,
    setBy: ["ADMIN", "AGENT"],
  },
  AGENT_SUB_PRE_ORDER_BASE_PRICE: {
    value: "AGENT_SUB_PRE_ORDER_BASE_PRICE",
    label: "Sub-agent pre-order base cost",
    scope: "sub-agent-cost",
    isPreorder: true,
    setBy: ["ADMIN", "AGENT"],
  },

  // Agent sets for a specific sub-agent
  AGENT_SUB_CUSTOM_PRICE: {
    value: "AGENT_SUB_CUSTOM_PRICE",
    label: "Sub-agent cost (individual)",
    scope: "sub-agent-cost-override",
    isPreorder: false,
    setBy: ["ADMIN", "AGENT"],
  },
  AGENT_SUB_PRE_ORDER_CUSTOM_PRICE: {
    value: "AGENT_SUB_PRE_ORDER_CUSTOM_PRICE",
    label: "Sub-agent pre-order cost (individual)",
    scope: "sub-agent-cost-override",
    isPreorder: true,
    setBy: ["ADMIN", "AGENT"],
  },

  // Sub-agent sets for their walk-in customers
  SUB_AGENT_CUSTOMER_PRICE: {
    value: "SUB_AGENT_CUSTOMER_PRICE",
    label: "Customer price",
    scope: "customer-facing",
    isPreorder: false,
    setBy: ["ADMIN", "SUB_AGENT"],
  },
  SUB_AGENT_PRE_ORDER_CUSTOMER_PRICE: {
    value: "SUB_AGENT_PRE_ORDER_CUSTOMER_PRICE",
    label: "Pre-order customer price",
    scope: "customer-facing",
    isPreorder: true,
    setBy: ["ADMIN", "SUB_AGENT"],
  },
};

export const priceTypeMeta = (value) =>
  Object.values(PRICE_TYPES).find((t) => t.value === value) || {
    value,
    label: value,
    scope: "unknown",
    isPreorder: false,
    setBy: [],
  };

/**
 * Human-readable label for a variant key, or "All variants" for the
 * empty / product-level case.
 *
 *   ""                    → "All variants"
 *   "Color=Red|Size=L"    → "Color: Red · Size: L"
 */
export const variantKeyLabel = (key) => {
  if (!key) return "All variants";
  return key
    .split("|")
    .map((pair) => {
      const eq = pair.indexOf("=");
      if (eq <= 0) return pair;
      return `${pair.slice(0, eq)}: ${pair.slice(eq + 1)}`;
    })
    .join(" · ");
};

/**
 * Save a price override.
 *
 * Because the backend uses update_or_create keyed on
 * (product, variant_key, user, price_type), we can always POST — no
 * need to know in advance whether a row exists. This removes the
 * entire "should I PATCH or POST?" branch from the UI.
 *
 * @param {function} authFetch
 * @param {object}   payload
 *   { product, variant_key?, user?, price_type, price, notes? }
 * @returns {Promise<object>} resolved with the saved pricing object
 * @throws  {Error} with a human-readable message on any failure
 */
export const savePricing = async (authFetch, payload) => {
  // Normalize variant_key: omitted and "" both mean product-level.
  const body = { ...payload };
  if (body.variant_key == null) body.variant_key = "";
  const res = await authFetch("/marketplace/pricing/", {
    method: "POST",
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(readApiError(data, "Could not save price"));
  }
  return data.data;
};

/**
 * Update an existing price override. Only price + notes can change.
 * The variant_key is part of the row's identity and is not editable.
 */
export const updatePricing = async (authFetch, pricingId, { price, notes }) => {
  const res = await authFetch(`/marketplace/pricing/${pricingId}/`, {
    method: "PATCH",
    body: JSON.stringify({ price, notes }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(readApiError(data, "Could not update price"));
  }
  return data.data;
};

/**
 * Delete a price override. Fallback chain takes over from the next level up.
 */
export const deletePricing = async (authFetch, pricingId) => {
  const res = await authFetch(`/marketplace/pricing/${pricingId}/`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(readApiError(data, "Could not remove price"));
  }
  return true;
};

/**
 * Fetch a page of pricing overrides. Server filters by query params.
 *
 * Filters may include `variant_key` to scope to a specific variant.
 * Passing `""` explicitly scopes to product-level overrides only.
 * Omitting `variant_key` returns all overrides for the product.
 */
export const listPricing = async (authFetch, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    // Allow explicit "" for variant_key to filter product-level rows.
    if (k === "variant_key" && v === "") {
      params.set(k, "");
      return;
    }
    if (v !== "") params.set(k, v);
  });
  const qs = params.toString();
  const res = await authFetch(`/marketplace/pricing/${qs ? `?${qs}` : ""}`);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(readApiError(data, "Could not load pricing"));
  }
  return data.data || [];
};

/**
 * Extract the human-readable error message from any of the
 * pricing endpoint's error shapes.
 */
const readApiError = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  const errs = data.errors;
  if (errs) {
    const parts = [];
    for (const [key, val] of Object.entries(errs)) {
      const arr = Array.isArray(val) ? val : [val];
      arr.forEach((msg) => {
        if (key === "non_field_errors" || key === "detail") parts.push(msg);
        else parts.push(`${key.replace(/_/g, " ")}: ${msg}`);
      });
    }
    if (parts.length) return parts.join(" ");
  }
  return fallback;
};

/**
 * Which price types can the current role create for a given context?
 *
 * @param {string} role        "ADMIN" | "AGENT" | "SUB_AGENT" | ...
 * @param {"customer"|"subAgent"} context
 * @returns {object[]} list of price-type metas
 */
export const availablePriceTypes = (role, context) => {
  if (context === "customer") {
    if (role === "ADMIN" || role === "AGENT") {
      return [
        PRICE_TYPES.AGENT_CUSTOMER_PRICE,
        PRICE_TYPES.AGENT_PRE_ORDER_CUSTOMER_PRICE,
      ];
    }
    if (role === "SUB_AGENT") {
      return [
        PRICE_TYPES.SUB_AGENT_CUSTOMER_PRICE,
        PRICE_TYPES.SUB_AGENT_PRE_ORDER_CUSTOMER_PRICE,
      ];
    }
  }
  if (context === "subAgent" && (role === "ADMIN" || role === "AGENT")) {
    return [
      PRICE_TYPES.AGENT_SUB_BASE_PRICE,
      PRICE_TYPES.AGENT_SUB_PRE_ORDER_BASE_PRICE,
      PRICE_TYPES.AGENT_SUB_CUSTOM_PRICE,
      PRICE_TYPES.AGENT_SUB_PRE_ORDER_CUSTOM_PRICE,
    ];
  }
  return [];
};

/* ══════════════════════════════════════════════════════════
   Variant awareness helpers
   ══════════════════════════════════════════════════════════ */

/**
 * Group an array of pricing overrides by variant_key.
 * Product-level rows (variant_key === "") go under the "" bucket.
 *
 * @returns {Map<string, object[]>} key → array of overrides for that key
 */
export const groupOverridesByVariant = (overrides) => {
  const map = new Map();
  (overrides || []).forEach((o) => {
    const key = o.variant_key || "";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(o);
  });
  return map;
};

/**
 * Resolve the effective price a caller would pay for a product, given
 * the pricing overrides visible to them and the caller's role.
 *
 * When variant_key is provided, the resolver first looks for a
 * variant-level override at that key, then falls back to the
 * product-level override, then to the product's base prices.
 *
 * Mirrors the backend's resolve_marketplace_price chain.
 *
 * @param {object}  product          product object with base_price_* fields
 * @param {object[]} overrides       pricing rows (already filtered to this product)
 * @param {string}  userRole         "AGENT" | "SUB_AGENT" | ...
 * @param {string} [variantKey]      "" or undefined for product-level
 * @returns {number|null}            resolved price, or null if unresolvable
 */
export const resolveEffectivePrice = (
  product,
  overrides,
  userRole,
  variantKey = ""
) => {
  const normalizedKey = variantKey || "";
  const find = (type, key = normalizedKey) =>
    overrides.find(
      (o) => o.price_type === type && (o.variant_key || "") === key
    )?.price ?? null;

  // Product-level fallbacks are used if the variant has no override.
  const findWithFallback = (type) =>
    find(type, normalizedKey) ?? find(type, "");

  // Also try to read a variant-specific base price from the product's
  // variants array (before any override is applied).
  const variantBasePrice = (field) => {
    if (!normalizedKey || !Array.isArray(product?.variants)) return null;
    const v = product.variants.find((x) => x.key === normalizedKey);
    return v ? Number(v[field]) : null;
  };

  if (userRole === "SUB_AGENT") {
    return (
      findWithFallback("SUB_AGENT_CUSTOMER_PRICE") ??
      findWithFallback("AGENT_SUB_CUSTOM_PRICE") ??
      findWithFallback("AGENT_SUB_BASE_PRICE") ??
      findWithFallback("ADMIN_AGENT_CUSTOM") ??
      variantBasePrice("base_price_agent") ??
      product.base_price_agent
    );
  }

  if (userRole === "AGENT") {
    return (
      findWithFallback("AGENT_CUSTOMER_PRICE") ??
      findWithFallback("ADMIN_AGENT_CUSTOM") ??
      variantBasePrice("base_price_agent") ??
      product.base_price_agent
    );
  }

  return (
    variantBasePrice("base_price_customer") ?? product.base_price_customer
  );
};

/**
 * Pre-order equivalent of resolveEffectivePrice.
 * Same chain but using `*_PRE_ORDER_*` types and pre_order_* base fields.
 */
export const resolveEffectivePreOrderPrice = (
  product,
  overrides,
  userRole,
  variantKey = ""
) => {
  const normalizedKey = variantKey || "";
  const find = (type, key = normalizedKey) =>
    overrides.find(
      (o) => o.price_type === type && (o.variant_key || "") === key
    )?.price ?? null;

  const findWithFallback = (type) =>
    find(type, normalizedKey) ?? find(type, "");

  const variantBasePrice = (field) => {
    if (!normalizedKey || !Array.isArray(product?.variants)) return null;
    const v = product.variants.find((x) => x.key === normalizedKey);
    return v ? Number(v[field]) : null;
  };

  if (userRole === "SUB_AGENT") {
    return (
      findWithFallback("SUB_AGENT_PRE_ORDER_CUSTOMER_PRICE") ??
      findWithFallback("AGENT_SUB_PRE_ORDER_CUSTOM_PRICE") ??
      findWithFallback("AGENT_SUB_PRE_ORDER_BASE_PRICE") ??
      findWithFallback("ADMIN_AGENT_PRE_ORDER_CUSTOM") ??
      variantBasePrice("pre_order_price_agent") ??
      product.pre_order_price_agent
    );
  }

  if (userRole === "AGENT") {
    return (
      findWithFallback("AGENT_PRE_ORDER_CUSTOMER_PRICE") ??
      findWithFallback("ADMIN_AGENT_PRE_ORDER_CUSTOM") ??
      variantBasePrice("pre_order_price_agent") ??
      product.pre_order_price_agent
    );
  }

  return (
    variantBasePrice("pre_order_price_customer") ??
    product.pre_order_price_customer
  );
};